<template>
    <div class="px-6 py-5 overflow-y-auto right-content-grid-vs h-[calc(100vh-309px)]">
        <div class="text-[#414141] font-sans mb-6">
            <h3 class="text-[1.25rem] leading-[1.75rem] mb-[1rem] font-semibold">
                Congratulations!
            </h3>

            <p class="text-[14px] leading-[22px] mb-[1rem]">
                Your organization now has the foundational elements of a sustainable
                fundraising plan. The fundraising goals, budgets, and narrative that
                you have developed can be repurposed to create a compelling case for
                support, grant application narrative, and other material that you can
                use to promote your organization and fundraise.
            </p>

            <p class="text-[14px] leading-[22px] mb-[1rem]">
                The most important thing to remember is to stick to the goals within this Toolkit until you have raised the money that you need to undertake the projects and programs you have articulated.
            </p>

            <p class="text-[14px] leading-[22px] font-semibold">
                We look forward to hearing about your fundraising success!
            </p>
        </div>

        <div class="bg-white rounded-2xl border border-[#E5E7EB] shadow-[0px_8px_32px_rgba(0,0,0,0.08)] p-4 md:p-6">
            <div class="flex items-center justify-end gap-3 mb-4">
                <div v-if="isLoading" class="text-[13px] md:text-[14px] text-[#666666]">
                    Loading organization name...
                </div>

                <button
                    type="button"
                    class="inline-flex items-center justify-center rounded-xl bg-[#00423D] px-4 py-2 text-[13px] md:text-[14px] font-semibold text-white transition-colors hover:bg-[#00352f] disabled:cursor-not-allowed disabled:opacity-60"
                    :disabled="isLoading"
                    @click="downloadCertificate"
                >
                    Download Certificate
                </button>
            </div>

            <div v-if="errorMessage" class="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-amber-800 text-[14px] leading-[20px]">
                {{ errorMessage }}
            </div>

            <div class="certificate-shell">
                <button
                    ref="certificateRef"
                    type="button"
                    class="certificate-frame group"
                    :aria-label="`Click to view certificate for ${organizationName || 'your organization'}`"
                    title="Click to view certificate"
                    @click="openCertificate"
                >
                    <img
                        class="certificate-image"
                        src="/images/Completion Certificate.png"
                        alt="Completion certificate"
                    />

                    <div class="certificate-name-layer" aria-hidden="true">
                        <p
                            class="certificate-org-name font-minion font-semibold text-[#00423D]"
                            :title="organizationName"
                        >
                            {{ organizationName || 'Your Organization' }}
                        </p>
                    </div>
                </button>
            </div>
        </div>

        <Teleport to="body">
            <transition name="certificate-modal-fade">
                <div
                    v-if="isCertificateOpen"
                    class="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 md:p-6"
                    tabindex="0"
                    role="button"
                    aria-label="Close certificate preview"
                    @click.self="closeCertificate"
                    @keydown.enter.self="closeCertificate"
                    @keydown.space.prevent.self="closeCertificate"
                >
                    <div class="certificate-modal-inner">
                        <button
                            type="button"
                            class="absolute right-2 top-2 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/95 text-[#00423D] shadow-lg transition-transform hover:scale-105"
                            aria-label="Close certificate preview"
                            @click="closeCertificate"
                        >
                            <span class="text-2xl leading-none">&times;</span>
                        </button>

                        <div class="certificate-shell">
                            <div class="certificate-frame certificate-frame--modal">
                                <img
                                    class="certificate-image certificate-image--modal"
                                    src="/images/Completion Certificate.png"
                                    alt="Completion certificate preview"
                                />

                                <div class="certificate-name-layer" aria-hidden="true">
                                    <p class="certificate-org-name font-minion font-semibold text-[#00423D]">
                                        {{ organizationName || 'Your Organization' }}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </transition>
        </Teleport>
    </div>
</template>

<script setup>
import {
  computed, nextTick, onBeforeUnmount, onMounted, ref, watch,
} from 'vue';
import { useAuthStore } from '@/stores/auth';
import global from '@/global';
import html2canvas from 'html2canvas';
import { useStepValidation } from '@/composables/useStepValidation';

const authStore = useAuthStore();
const isLoading = ref(true);
const errorMessage = ref('');
const organizationName = ref('');
const certificateRef = ref(null);
const isCertificateOpen = ref(false);

const certificateFileName = computed(() => `${(organizationName.value || 'certificate').replace(/\s+/g, '-').toLowerCase()}-completion-certificate.png`);

async function fetchOrganizationName() {
  if (!authStore.user?.accessToken) {
    errorMessage.value = 'Unable to load organization data. Please sign in again.';
    isLoading.value = false;
    return;
  }

  try {
    const res = await fetch(`${global.API_URL}/organizations`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${authStore.user.accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!res.ok) {
      throw new Error(`HTTP error! Status: ${res.status}`);
    }

    const response = await res.json();
    const organization = response?.data || response;

    organizationName.value = organization?.name?.trim() || '';
    if (!organizationName.value) {
      errorMessage.value = 'Organization name was not found. Showing a placeholder on the certificate.';
    }
  } catch (error) {
    errorMessage.value = 'Unable to load organization name. Showing a placeholder on the certificate.';
    organizationName.value = '';
  } finally {
    isLoading.value = false;
  }
}

async function downloadCertificate() {
  if (!certificateRef.value) {
    return;
  }

  const canvas = await html2canvas(certificateRef.value, {
    scale: 2,
    useCORS: true,
    backgroundColor: '#ffffff',
  });

  const image = canvas.toDataURL('image/png');
  const link = document.createElement('a');

  link.href = image;
  link.download = certificateFileName.value;
  link.click();
}

function openCertificate() {
  isCertificateOpen.value = true;
}

function closeCertificate() {
  isCertificateOpen.value = false;
}

function handleKeydown(event) {
  if (event.key === 'Escape' && isCertificateOpen.value) {
    closeCertificate();
  }
}

watch(isCertificateOpen, (isOpen) => {
  document.body.style.overflow = isOpen ? 'hidden' : '';
});

onMounted(async () => {
  window.addEventListener('keydown', handleKeydown);
  await authStore.fetchUserData();
  await fetchOrganizationName();
  await nextTick();
});

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleKeydown);
  document.body.style.overflow = '';
});

// Use the validation composable with default settings
useStepValidation();
</script>

<style scoped>
.certificate-shell {
    width: 100%;
    display: flex;
    justify-content: center;
}

.certificate-frame {
    position: relative;
    width: min(100%, 1200px);
    cursor: zoom-in;
    border: 0;
    background: transparent;
    padding: 0;
    appearance: none;
    text-align: inherit;
    display: block;
}

.certificate-frame--modal {
    width: 100%;
    cursor: default;
}

.certificate-image {
    display: block;
    width: 100%;
    height: auto;
}

.certificate-image--modal {
    width: auto;
    height: auto;
    max-width: 100%;
    max-height: calc(94vh - 2rem);
    object-fit: contain;
    display: block;
    margin: 0 auto;
}

.certificate-name-layer {
    position: absolute;
    left: 0;
    right: 0;
    top: 30%;
    bottom: 38%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 6%;
    pointer-events: none;
}

.certificate-org-name {
    margin: 0;
    width: min(62%, 720px);
    text-align: center;
    font-size: clamp(18px, 2vw, 30px);
    line-height: 1.15;
    letter-spacing: 0.01em;
    overflow-wrap: anywhere;
    word-break: normal;
    text-wrap: balance;
}

.certificate-frame:hover .certificate-image,
.certificate-frame:focus-visible .certificate-image {
    filter: brightness(0.96);
}

/* Modal inner wrapper — sized to fit screen, no scroll */
.certificate-modal-inner {
    position: relative;
    width: min(92vw, 1450px);
    max-height: 94vh;
    display: flex;
    align-items: center;
    justify-content: center;
}

@media (max-width: 768px) {
    .certificate-name-layer {
        top: 29%;
        bottom: 39%;
        padding: 0 4%;
    }

    .certificate-org-name {
        width: min(78%, 560px);
        font-size: clamp(15px, 4vw, 22px);
    }

    .certificate-image--modal {
        max-height: calc(90vh - 1rem);
    }
}

.certificate-modal-fade-enter-active,
.certificate-modal-fade-leave-active {
    transition: opacity 0.2s ease;
}

.certificate-modal-fade-enter-from,
.certificate-modal-fade-leave-to {
    opacity: 0;
}
</style>